import { useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { Briefcase, Lock } from "lucide-react";
import BusinessTypeSelector, {
  BUSINESS_TYPE_LABEL,
} from "@/components/merchant/BusinessTypeSelector";
import SaasCheckoutCurrencyCard from "@/components/merchant/SaasCheckoutCurrencyCard";

export default function StoreProfile() {
  const [form, setForm] = useState(null);
  const [cats, setCats] = useState([]);
  const [btOpen, setBtOpen] = useState(false);
  const [btLockedView, setBtLockedView] = useState(false);

  useEffect(() => {
    api.get("/merchant/store").then((r) => setForm(r.data));
    api.get("/categories").then((r) => setCats(r.data));
  }, []);

  const save = async (e) => {
    e.preventDefault();
    // NB: `merchant_type` is deliberately NOT written from this form —
    // it flows through the dedicated BusinessTypeSelector modal so we
    // never silently mutate it on a routine profile save.
    try {
      const {
        merchant_type: _mt,
        merchant_type_locked: _l,
        checkout_currency: _cc,
        supported_checkout_currencies: _s,
        primary_checkout_currency: _p,
        usd_checkout_enabled: _u,
        usd_checkout_confirmed_at: _uc,
        ...profile
      } = form;
      await api.put("/merchant/store", profile);
      toast.success("Store profile updated");
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to save");
    }
  };

  if (!form) return <div>Loading...</div>;
  const mtValue = form.merchant_type || "UNCLASSIFIED";
  const mtLabel = BUSINESS_TYPE_LABEL[mtValue] || "Unclassified";
  const mtLocked = !!form.merchant_type_locked;

  return (
    <div data-testid="merchant-store-profile">
      <h2 className="font-display text-3xl font-semibold tracking-tight">Store profile</h2>
      <p className="text-sm text-muted-foreground mt-1">This is how your business appears to Cyannect shoppers.</p>

      {/* SAAS-USD-BILLING-1 · Phase 0.5 — Business Type card (business-model
          discriminator, deliberately separate from the Category/Storefront
          category merchandising field below). */}
      <div className="pf-card p-6 mt-6 max-w-2xl" data-testid="sp-business-type-card">
        <div className="flex items-start gap-3">
          <div className="w-10 h-10 rounded-full bg-cyan-50 text-cyan-700 inline-flex items-center justify-center flex-shrink-0">
            <Briefcase className="w-5 h-5" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-xs font-semibold uppercase tracking-widest text-muted-foreground">
              Business Type
            </div>
            <div
              className="mt-1 font-display text-xl font-medium tracking-tight text-slate-900"
              data-testid="sp-business-type-value"
            >
              {mtLabel}
            </div>
            <p className="text-xs text-slate-500 mt-1">
              Your business model. Different from your Category / Storefront category below, which
              is a merchandising classification.
            </p>
            {mtValue === "UNCLASSIFIED" && (
              <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-lg px-3 py-2">
                Not set yet — Cyannect can't tailor your campaign and billing options until you
                pick one.
              </div>
            )}
          </div>
          <div className="flex flex-col items-end gap-2">
            {mtLocked && (
              <span
                className="inline-flex items-center gap-1 text-[10px] font-semibold uppercase tracking-widest text-slate-500 bg-slate-100 rounded-full px-2 py-1"
                data-testid="sp-business-type-locked-chip"
              >
                <Lock className="w-3 h-3" /> Locked
              </span>
            )}
            <button
              type="button"
              onClick={() => {
                setBtLockedView(mtLocked);
                setBtOpen(true);
              }}
              className={`px-3 py-1.5 rounded-full text-xs font-semibold ${
                mtLocked
                  ? "border border-slate-200 text-slate-700 hover:bg-slate-50"
                  : "pf-btn-primary"
              }`}
              data-testid={
                mtValue === "UNCLASSIFIED"
                  ? "sp-business-type-set"
                  : "sp-business-type-change"
              }
            >
              {mtLocked
                ? "View lock details"
                : mtValue === "UNCLASSIFIED"
                ? "Set Business Type"
                : "Change Business Type"}
            </button>
          </div>
        </div>
      </div>

      {/* SAAS-USD-BILLING-1 · Phase 1 — currency configuration for SaaS
          merchants. Renders itself only when merchant_type is SaaS; Retail
          UI stays completely unchanged. */}
      <SaasCheckoutCurrencyCard
        store={form}
        onUpdated={(next) => setForm((f) => ({ ...f, ...next }))}
      />

      <form onSubmit={save} className="pf-card p-6 mt-6 max-w-2xl">
        <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground">Store name</label>
        <input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="sp-name" />

        <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4">URL slug</label>
        <input required value={form.slug} onChange={(e) => setForm({ ...form, slug: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background font-mono text-sm" data-testid="sp-slug" />

        <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4">Description</label>
        <textarea value={form.description || ""} onChange={(e) => setForm({ ...form, description: e.target.value })} rows={3} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="sp-desc" />

        <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4">Website</label>
        <input value={form.website || ""} onChange={(e) => setForm({ ...form, website: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="sp-web" />

        <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4">Logo URL</label>
        <input value={form.logo_url || ""} onChange={(e) => setForm({ ...form, logo_url: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="sp-logo" />

        <label className="block text-xs font-semibold uppercase tracking-widest text-muted-foreground mt-4">Category</label>
        <select value={form.category_id || ""} onChange={(e) => setForm({ ...form, category_id: e.target.value })} className="mt-1 w-full px-3 py-2 rounded-md border border-border bg-background" data-testid="sp-cat">
          <option value="">Select category</option>
          {cats.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
        </select>
        <p className="text-[11px] text-slate-500 mt-1">
          Merchandising category — how your storefront appears in Cyannect's coupon &amp;
          discovery feeds. Not the same as Business Type above.
        </p>

        <div className="mt-6 flex justify-between items-center">
          <div className="text-sm">
            Status: <span className={`pf-chip ${form.status === "approved" ? "bg-[color:var(--pf-mint)] text-black" : "bg-secondary"}`}>{form.status}</span>
          </div>
          <button type="submit" className="pf-btn-primary px-4 py-2 rounded-md font-semibold" data-testid="sp-save">Save changes</button>
        </div>
      </form>

      <BusinessTypeSelector
        open={btOpen}
        onClose={() => {
          setBtOpen(false);
          setBtLockedView(false);
        }}
        currentValue={mtValue}
        locked={btLockedView}
        onSaved={(next, meta) =>
          setForm((f) =>
            f
              ? {
                  ...f,
                  merchant_type: next,
                  merchant_type_locked: meta?.locked ? true : f.merchant_type_locked,
                }
              : f
          )
        }
      />
    </div>
  );
}
